import CircleWithTickIcon from "@/assets/colorable/circle_with_tick.svg?react";
import CircleWithTimesIcon from "@/assets/colorable/circle_with_times.svg?react";
import {type FC} from "react";
import {useTranslation} from "react-i18next";

type TPermissionRole = "ADMIN" | "WRITE" | "PURCHASE" | "READ";

const ROLES = [
  {role: "ADMIN", translationKey: "admin_permissions"},
  {role: "WRITE", translationKey: "write_permissions"},
  {role: "PURCHASE", translationKey: "purchase_permissions"},
  {role: "READ", translationKey: "read_permissions"},
] as const;

const CAPABILITIES = [
  {translationKey: "view_campaigns_and_influencers", allowedRoles: ["ADMIN", "WRITE", "PURCHASE", "READ"]},
  {translationKey: "download_ad_codes", allowedRoles: ["ADMIN", "WRITE", "PURCHASE", "READ"]},
  {translationKey: "purchase_content_buyouts", allowedRoles: ["ADMIN", "WRITE", "PURCHASE"]},
  {translationKey: "manage_campaigns", allowedRoles: ["ADMIN", "WRITE"]},
  {translationKey: "invite_to_account", allowedRoles: ["ADMIN", "WRITE"]},
  {translationKey: "manage_permissions", allowedRoles: ["ADMIN", "WRITE"]},
  {translationKey: "manage_card_information", allowedRoles: ["ADMIN"]},
] as const;

const PermissionGuide: FC = () => {
  const {t} = useTranslation("multi_account");

  return (
    <div className="bg-neutral-white-dark @container overflow-x-auto rounded-lg p-4">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-full" />
            {ROLES.map(({role, translationKey}) => (
              <th
                key={role}
                scope="col"
                className="text-neutral-grey-dark text-2xs px-2 pb-3 align-bottom font-semibold tracking-wide uppercase @2xl:whitespace-nowrap">
                {t(translationKey)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {CAPABILITIES.map(({translationKey, allowedRoles}) => (
            <tr key={translationKey} className="border-neutral-grey-light-dark border-t">
              <th
                scope="row"
                className="text-neutral-black py-2 pr-3 text-left text-xs font-normal first-letter:uppercase">
                {t(translationKey)}
              </th>
              {ROLES.map(({role}) => {
                const can = (allowedRoles as readonly TPermissionRole[]).includes(role);

                return (
                  <td key={role} className="px-2 py-2">
                    <div className="flex justify-center">
                      {can ? (
                        <CircleWithTickIcon className="text-success-herbal size-3.5 shrink-0" />
                      ) : (
                        <CircleWithTimesIcon className="text-danger-raspberry size-3.5 shrink-0" />
                      )}
                      <span className="sr-only">{t(can ? "can" : "cant")}</span>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionGuide;
